import React from 'react';
import { SelectProps, Select as TUISelect } from '@apia/theme';
import { getVariant } from '@apia/theme';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { TDefaultFieldType } from './types';
import { FieldWrapper } from './FieldWrapper';
import { TId } from '@apia/util';
import { useFieldStatesClassNames } from '../hooks';

export type TOption = { value: TId; label: string };

export type TSelectProps = Omit<SelectProps, 'defaultValue'>;

export type TSelect = TSelectProps &
  TDefaultFieldType & {
    options: TOption[];
  };

export const Select = React.forwardRef<HTMLSelectElement, TSelect>(
  (
    {
      avoidSemicolon,
      className,
      formName,
      label,
      onChange: outerOnChange,
      options,
      /**
       * useField
       */
      avoidRegisterIfExists,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });

    const { updateValidations, onChange, isTouched, ...inputProps } =
      useField<string>({
        avoidRegisterIfExists,
        initialValue: initialValue ?? (value as string) ?? options[0]?.value,
        name,
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

    const stateClassName = useFieldStatesClassNames(name);
    return (
      <FieldWrapper
        name={name}
        label={label}
        className={`select ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
        title={props.title}
      >
        <TUISelect
          {...getVariant('forms.select')}
          {...props}
          {...inputProps}
          className={stateClassName}
          onChange={React.useCallback(
            (ev: React.ChangeEvent<HTMLSelectElement>) => {
              if (props.multiple) {
                onChange(
                  [...ev.target.options]
                    .filter((c) => c.selected)
                    .map((c) => c.value) as any,
                );
              } else {
                onChange(ev);
              }
              if (outerOnChange) {
                outerOnChange(ev);
              }
            },
            [onChange, outerOnChange, props.multiple],
          )}
          aria-label={props['aria-label'] ?? label}
          ref={ref}
          value={inputProps.value ?? ''}
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {props.children}
        </TUISelect>
      </FieldWrapper>
    );
  },
);

Select.displayName = 'Select';
